const exportToPDF = async (sortedData, tableColumns, entityName, exportFileName) => {
  try {
    // Dynamic import for pdfmake (will need to be installed)
    const pdfMake = await import('pdfmake/build/pdfmake');
    const vfs = await import('pdfmake/build/vfs_fonts');
    const dayjs = (await import('dayjs')).default;
    const { appConfig, getDateFormat } = await import('../../config/appConfig');
    
    pdfMake.default.vfs = vfs.vfs;
    
    // Prepare columns - include all except actions
    const columnsToExtract = tableColumns
      .filter(col => col && col.key && col.key !== 'actions')
      .map(col => ({
        headerName: col.header || col.label || col.key || 'Unknown',
        field: col.key,
        displayField: col.displayField,
        cellRenderer: col.cellRenderer,
        dateFormat: col.dateFormat || getDateFormat('table') || appConfig.dateFormat
      }));

    // Helper function to format dates
    const formatDateByPattern = (date, pattern) => {
      if (!date) return '';
      try {
        const d = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(d.getTime())) return '';
        const dd = String(d.getDate()).padStart(2, '0');
        const MM = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = String(d.getFullYear());
        const HH = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const formatPattern = pattern || getDateFormat('table') || appConfig.dateFormat;
        return formatPattern
          .replace(/dd/g, dd)
          .replace(/MM/g, MM)
          .replace(/yyyy/g, yyyy)
          .replace(/HH/g, HH)
          .replace(/mm/g, mm) || d.toLocaleDateString();
      } catch {
        return '';
      }
    };

    // Helper function to format array/multiselect values
    const formatArrayValue = (value, column, row) => {
      if (!value && !(column.displayField && row && row[column.displayField])) return '';
      
      let arrayValue = value;
      if (column.displayField && row && row[column.displayField]) {
        arrayValue = row[column.displayField];
      }
      
      if (!Array.isArray(arrayValue)) {
        if (typeof arrayValue === 'string' && arrayValue.trim().startsWith('[')) {
          try {
            arrayValue = JSON.parse(arrayValue);
          } catch {
            return value;
          }
        } else {
          return value;
        }
      }
      
      if (arrayValue.length === 0) return '';
      
      if (arrayValue.length > 0 && typeof arrayValue[0] === 'object') {
        const names = arrayValue.map(item => {
          return item.userDisplayName 
            || item.name 
            || item.title 
            || item.label 
            || (item.userFirstName && item.userLastName ? `${item.userFirstName} ${item.userLastName}` : null)
            || '';
        }).filter(name => name);
        
        return names.join(', ');
      }
      
      return arrayValue.join(', ');
    };

    // Helper to format cell value
    const formatCellValue = (value, column, row) => {
      if (value === undefined || value === null) {
        if (column.displayField && row && row[column.displayField] !== undefined) {
          value = row[column.displayField];
        } else {
          return '';
        }
      }

      // Format dates
      if (column.cellRenderer === 'date' && value) {
        return formatDateByPattern(value, column.dateFormat);
      }
      
      // Format arrays
      if (column.cellRenderer === 'array' || Array.isArray(value)) {
        return formatArrayValue(value, column, row);
      }
      
      if (column.displayField && row[column.displayField] && Array.isArray(row[column.displayField])) {
        return formatArrayValue(row[column.displayField], column, row);
      }
      
      // Handle objects
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (value.name || value.title || value.label) {
          return value.name || value.title || value.label;
        }
        return JSON.stringify(value);
      }
      
      return value?.toString() || '';
    };

    // Determine layout based on number of columns
    const useCardLayout = columnsToExtract.length > 6; // Use card layout if more than 6 columns
    
    let content;
    
    if (useCardLayout) {
      // Card-based layout - each record as a card with label-value pairs
      content = sortedData.map((row, index) => {
        const recordNumber = index + 1;
        const cardContent = [];
        
        // Card header with record number
        cardContent.push({
          text: `Record #${recordNumber}`,
          style: 'cardHeader',
          margin: [0, 0, 0, 10]
        });
        
        // Group columns into 2 columns for better space usage
        const fieldsPerRow = 2;
        for (let i = 0; i < columnsToExtract.length; i += fieldsPerRow) {
          const rowFields = columnsToExtract.slice(i, i + fieldsPerRow);
          
          const columns = rowFields.map((col, colIndex) => {
            const value = formatCellValue(row[col.field], col, row);
            const columnStack = {
              stack: [
                {
                  text: col.headerName,
                  style: 'fieldLabel',
                  margin: [0, 0, 0, 4]
                },
                {
                  text: value || '—',
                  style: 'fieldValue',
                  margin: [0, 0, 0, 8]
                }
              ],
              width: '*'
            };
            
            // Add right border to first column as separator
            if (colIndex === 0 && rowFields.length === fieldsPerRow) {
              columnStack.border = [false, false, true, false];
              columnStack.borderColor = '#d0d0d0';
              columnStack.borderWidth = 0.5;
              columnStack.margin = [0, 0, 10, 0];
            } else if (colIndex > 0) {
              columnStack.margin = [10, 0, 0, 0];
            }
            
            return columnStack;
          });
          
          // Fill remaining space if odd number of fields
          while (columns.length < fieldsPerRow) {
            columns.push({ text: '', width: '*' });
          }
          
          cardContent.push({
            columns: columns,
            margin: [0, 0, 0, 0]
          });
        }
        
        return {
          stack: [
            {
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: 515,
                  h: 0,
                  r: 4,
                  lineWidth: 1,
                  lineColor: '#e0e0e0'
                }
              ],
              absolutePosition: { x: 40, y: 0 }
            },
            {
              stack: cardContent,
              margin: [15, 15, 15, 15]
            }
          ],
          margin: [0, 0, 0, 15],
          pageBreak: index > 0 && index % 2 === 0 ? 'before' : 'avoid' // 2 records per page
        };
      });
    } else {
      // Traditional table layout for fewer columns
      const headers = columnsToExtract.map(column => ({
        text: column.headerName,
        style: 'header',
        alignment: 'center'
      }));

      const data = sortedData.map(row =>
        columnsToExtract.map(column => ({
          text: formatCellValue(row[column.field], column, row) || '',
          style: 'cell',
          margin: [0, 1]
        }))
      );

      content = [{
        table: {
          headerRows: 1,
          widths: columnsToExtract.map(() => 'auto'),
          body: [headers, ...data],
        },
        layout: {
          fillColor: (rowIndex) => rowIndex === 0 ? '#f0f0f0' : (rowIndex % 2 === 0 ? '#f9f9f9' : null),
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#cccccc',
          vLineColor: () => '#cccccc',
          paddingTop: () => 4,
          paddingBottom: () => 4,
          paddingLeft: () => 4,
          paddingRight: () => 4
        }
      }];
    }

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: useCardLayout ? 'portrait' : 'landscape',
      pageMargins: [40, 60, 40, 60],
      header: {
        text: `${entityName} Export`,
        style: 'title',
        margin: [0, 10, 0, 0]
      },
      footer: (currentPage, pageCount) => ({
        columns: [
          {
            text: `Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
            alignment: 'left',
            style: 'footer',
            margin: [40, 0, 0, 0]
          },
          {
            text: `Page ${currentPage} of ${pageCount} | Total Records: ${sortedData.length}`,
            alignment: 'right',
            style: 'footer',
            margin: [0, 0, 40, 0]
          }
        ]
      }),
      content: [
        {
          text: `Total Records: ${sortedData.length}`,
          style: 'subtitle',
          margin: [0, 0, 0, 15]
        },
        ...content
      ],
      styles: {
        title: {
          fontSize: 18,
          bold: true,
          alignment: 'center',
          color: '#1a1a1a'
        },
        subtitle: {
          fontSize: 11,
          color: '#666666',
          alignment: 'center'
        },
        header: {
          fontSize: 10,
          bold: true,
          alignment: 'center',
          color: '#ffffff',
          fillColor: '#4a5568'
        },
        cell: {
          fontSize: 9,
          lineHeight: 1.2
        },
        cardHeader: {
          fontSize: 14,
          bold: true,
          color: '#2c3e50',
          decoration: 'underline'
        },
        fieldLabel: {
          fontSize: 9,
          bold: true,
          color: '#555555'
        },
        fieldValue: {
          fontSize: 10,
          color: '#333333',
          lineHeight: 1.4
        },
        footer: {
          fontSize: 8,
          color: '#666666'
        }
      },
      defaultStyle: {
        fontSize: 10,
        lineHeight: 1.4,
        color: '#333333'
      }
    };

    pdfMake.default.createPdf(docDefinition).download(`${exportFileName}.pdf`);
    return { success: true };
  } catch (error) {
    console.error('PDF export error:', error);
    console.log('pdfMake library not available, using HTML fallback');
    // Fallback to HTML export
    exportToHTML(sortedData, tableColumns, entityName, exportFileName);
    return { success: true, fallback: 'html' };
  }
};

const exportToHTML = (sortedData, tableColumns, entityName, exportFileName) => {
  // Fallback HTML export for when pdfmake is not available
  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${entityName} Export</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .export-info { margin-bottom: 20px; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>${entityName} Export</h1>
        <div class="export-info">
          Generated on: ${new Date().toLocaleString()}<br>
          Total records: ${sortedData.length}
        </div>
        <table>
          <thead>
            <tr>
              ${tableColumns.filter(col => col.key !== 'actions').map(col => `<th>${col.header}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${sortedData.map(item => `
              <tr>
                ${tableColumns.filter(col => col.key !== 'actions').map(col => {
                  let value = item[col.key];
                  if (typeof value === 'object' && value !== null) {
                    value = JSON.stringify(value);
                  }
                  return `<td>${value || ''}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
  
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${exportFileName}.html`;
  a.click();
  URL.revokeObjectURL(url);
};

export { exportToPDF };
